<template>
  <div class="service-outcome-form">
    <FieldGroupWrapper>
      <FormDateField
        field-name="expense_date"
        :label="$t('Outcome.Date')"
        :value-format="DATE_FORMAT"
        required />
      <FormSearchSelectField
        field-name="expense_type_id"
        field-object-name="expense_type"
        :model-for-use="Expense"
        :label="$t('Outcome.Type')"
        :custom-label="`+ ${$t('Services.AddExpenseType')}`"
        method-name="getExpenseTypes"
        @openModal="openNestedModal"
        required>
      </FormSearchSelectField>
      <FormTextField
        :label="$t('Outcome.Recipient')"
        field-name="recipient"
        :placeholder="$t('Outcome.RecipientPlaceholder')"
        required
        maxlength="255" />
      <FormTextField
        field-name="amount"
        :label="$t('Outcome.Summary')"
        :placeholder="$t('Outcome.SettingSum')"
        mask-name="int-12"
        required />
      <FormCheckboxField field-name="auto_accept" :label="$t('Outcome.Auto')" />
    </FieldGroupWrapper>
    <FormActionsWrapper>
      <ElButton type="primary" @click="submitForm">{{ `+ ${$t('Outcome.Add')}` }}</ElButton>
    </FormActionsWrapper>
    <OutcomeTypeModal
      v-if="isNestedModalVisible"
      v-model="isNestedModalVisible"
      @add-outcome="addTypeOutcome"
      append-to-body />
  </div>
</template>

<script lang="ts">
export default {
  name: 'OutcomeForm',
  inheritAttrs: false,
  customOptions: {},
};
</script>

<script lang="ts" setup>
import { ref } from 'vue';
import { useField, useForm } from 'vee-validate';

import { Expense, ExpenseFormData } from '@/models/accounting/Expense.model';
import { ExpenseRequest, ExpenseTypeResponse } from '@/types/api';
import { validationSchema } from '@/components/accounting/modals/ServiceOutcomeModal/OutcomeForm/validationSchema';
import { GlobalModalInstance } from '~widgets/GlobalModalAndDrawer';
import { DATE_FORMAT } from '~shared/config';
import FormActionsWrapper from '@/components/common/form/FormActionsWrapper/index.vue';
import FormTextField from '@/components/common/form/ui/FormTextField/index.vue';
import FieldGroupWrapper from '@/components/common/form/FieldGroupWrapper/index.vue';
import FormDateField from '@/components/common/form/ui/FormDateField/index.vue';
import FormSearchSelectField from '@/components/common/form/ui/FormSearchSelectField/index.vue';
import FormCheckboxField from '@/components/common/form/ui/FormCheckboxField/index.vue';
import OutcomeTypeModal from '@/components/accounting/modals/ServiceOutcomeModal/OutcomeTypeModal/Index.vue';

const props = defineProps<{
  formData: ExpenseFormData;
}>();

const emit = defineEmits(['close:modal', 'action']);

const isNestedModalVisible = ref(false);

// eslint-disable-next-line vue/no-setup-props-destructure
const { handleSubmit } = useForm({
  initialValues: props.formData,
  validationSchema,
  validateOnMount: false,
});

const openNestedModal = () => {
  isNestedModalVisible.value = true;
};

const { setValue } = useField<ExpenseFormData['expense_type_id']>('expense_type_id');
const { setValue: setObjectValue } = useField<ExpenseFormData['expense_type']>('expense_type');
const addTypeOutcome = (outcomeType: ExpenseTypeResponse['data']) => {
  setValue(outcomeType.id);
  setObjectValue(outcomeType);
};

const submitForm = handleSubmit(
  async (values) => {
    const requestData: ExpenseRequest = {
      recipient: values.recipient,
      amount: values.amount ?? 0,
      expense_type_id: values.expense_type?.id,
      expense_date: values.expense_date,
      auto_accept: values.auto_accept,
    };

    const response = await Expense.createItem(requestData);
    const responseData = response?.data ?? {};

    emit(
      'action',
      new GlobalModalInstance({
        name: props.formData.id ? 'updated' : 'created',
        data: responseData,
      })
    );
    emit('close:modal');
  },
  (ctx) => {
    // eslint-disable-next-line no-console
    console.log('invalid submit', ctx.errors);
  }
);
</script>

<style lang="scss" src="./index.scss" />
<i18n src="@/locales/validation.locales.json" />
<i18n src="@/locales/accounting.locales.json" />
